import InternalAdminLogo from '@/assets/icons/InternalAdminLogo';
import Button from '@/components/Common/Button/Button';
import FormItem from '@/components/Common/FormItem/FormItem';
import COLORS from '@/constants/colors';
import { DashboardUserType } from '@/utils/enums';
import { ErrorOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import { NEW_SELLER_FORM_LINK } from './formUtils';
import { useLoginViewController } from './LoginViewController';

export interface LoginSellerProps {
  loginType: DashboardUserType;
}

const loginMessages = {
  [DashboardUserType.SELLER]: 'Inicia sesión como vendedor',
  [DashboardUserType.ADMIN]: 'Inicia sesión como admin',
};

export function LoginForm({ loginType }: LoginSellerProps) {
  const { uiState, actions } = useLoginViewController(loginType);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: { xs: '100%', md: '50%' },
        justifyContent: 'center',
        background: '#fcfcfd',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: 2, md: 4 },
          backgroundColor: COLORS.white,
          width: '100%',
          height: { xs: 'max-content', md: '100%' },
        }}
      >
        <form onSubmit={actions.onSubmit}>
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: { xs: '0px 0px', md: '100px 0px' },
              width: '100%',
              maxWidth: '400px',
              margin: '0 auto',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  display: { xs: 'inline-block', md: 'none' },
                  marginBottom: '20px',
                  maxWidth: '150px',
                  width: '100%',
                  height: 'auto',
                  textAlign: 'center',
                }}
              >
                <InternalAdminLogo />
              </Box>

              <Typography
                sx={{
                  color: COLORS.olive,
                  fontWeight: 600,
                  fontSize: '24px',
                  lineHeight: '24px',
                  marginBottom:
                    loginType === DashboardUserType.SELLER ? '0px' : '20px',
                }}
              >
                {loginMessages[loginType] || ''}
              </Typography>
              {loginType === DashboardUserType.SELLER && (
                <Box
                  sx={{
                    fontWeight: 400,
                    fontSize: '14px',
                    color: COLORS.gray_600,
                    paddingTop: '15px',
                    paddingBottom: '35px',
                  }}
                >
                  ¿Eres nuevo?{' '}
                  <Box
                    component="a"
                    href={NEW_SELLER_FORM_LINK}
                    sx={{
                      textDecoration: 'underline',
                      textUnderlinePosition: 'under',
                      fontFamily: 'Inter',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: COLORS.gray_600,
                    }}
                  >
                    Crea cuenta
                  </Box>
                </Box>
              )}
            </Box>

            <Stack spacing={2} sx={{ mx: { sm: '10px' }, width: '100%' }}>
              <Stack
                spacing={3}
                sx={{ display: 'block', color: COLORS.gray_700, width: '100%' }}
              >
                <FormItem helperText={uiState.userError}>
                  <FormControl fullWidth>
                    <FormLabel
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: COLORS.gray_700,
                        marginBottom: '8px',
                      }}
                    >
                      Correo electrónico
                    </FormLabel>
                    <TextField
                      fullWidth
                      placeholder="ej: nombre@gmail.com"
                      value={uiState.user}
                      onChange={(e) => actions.onUserChanged(e.target.value)}
                      error={Boolean(uiState.userError)}
                      InputProps={{
                        endAdornment: uiState.userError && (
                          <InputAdornment position="end">
                            <ErrorOutline color="error" />
                          </InputAdornment>
                        ),
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                          },
                          '& input': {
                            WebkitBoxShadow: `0 0 0 1000px ${COLORS.white} inset`,
                            backgroundColor: `${COLORS.white}`,
                            fontSize: '16px',
                          },
                          '& input:-internal-autofill-selected': {
                            WebkitBoxShadow: `0 0 0 1000px ${COLORS.white} inset`,
                            backgroundColor: `${COLORS.white}`,
                          },
                          '& input:-webkit-autofill': {
                            WebkitBoxShadow: `0 0 0 1000px ${COLORS.white} inset`,
                            backgroundColor: `${COLORS.white}`,
                          },
                        },
                      }}
                      inputProps={{
                        style: {
                          fontSize: '16px',
                        },
                      }}
                    />
                  </FormControl>
                </FormItem>

                <FormItem helperText={uiState.passwordError}>
                  <FormControl fullWidth>
                    <FormLabel
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: COLORS.gray_700,
                        marginBottom: '8px',
                      }}
                    >
                      Ingresa contraseña
                    </FormLabel>
                    <TextField
                      fullWidth
                      placeholder="6 caracteres"
                      type={uiState.passwordVisible ? 'text' : 'password'}
                      value={uiState.password}
                      onChange={(e) => actions.onPasswordChanged(e)}
                      error={Boolean(uiState.passwordError)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {uiState.passwordError ? (
                              <ErrorOutline color="error" />
                            ) : (
                              <IconButton
                                onMouseDown={actions.togglePasswordVisibility}
                                edge="end"
                              >
                                {uiState.passwordVisible ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            )}
                          </InputAdornment>
                        ),
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                          },
                          '& input': {
                            WebkitBoxShadow: `0 0 0 1000px ${COLORS.white} inset`,
                            backgroundColor: `${COLORS.white}`,
                            fontSize: '16px',
                          },
                          '& input:-internal-autofill-selected': {
                            WebkitBoxShadow: `0 0 0 1000px ${COLORS.white} inset`,
                            backgroundColor: `${COLORS.white}`,
                          },
                          '& input:-webkit-autofill': {
                            WebkitBoxShadow: `0 0 0 1000px ${COLORS.white} inset`,
                            backgroundColor: `${COLORS.white}`,
                          },
                        },
                      }}
                      inputProps={{
                        style: {
                          fontSize: '16px',
                        },
                      }}
                    />
                  </FormControl>
                </FormItem>
              </Stack>

              <Stack>
                <Box
                  sx={{
                    textAlign: 'center',
                    color: COLORS.blue_dark_500,
                    textDecoration: 'underline',
                    textUnderlinePosition: 'under',
                    fontWeight: 500,
                    paddingBottom: '20px',
                  }}
                >
                  {loginType === DashboardUserType.SELLER && (
                    <NavLink
                      to="/seller/request-reset-password"
                      style={{ color: 'inherit' }}
                    >
                      ¿Olvidaste tu contraseña?
                    </NavLink>
                  )}
                </Box>
              </Stack>

              <Stack spacing={4}>
                <Button
                  label={'Iniciar sesión'}
                  sx={{ borderRadius: '10px' }}
                  type="submit"
                  loading={uiState.isLoading}
                />
              </Stack>
            </Stack>
          </Stack>
        </form>
      </Box>
    </Box>
  );
}
